// --- Constantes monde 3D ---

// Hash stable pour assigner un avatar à un player.id. Même joueur → même avatar
// dans toutes les vues (lobby kiosk + jeu spectateur + jeu joueur). Collisions
// possibles avec > 24 joueurs concurrents, mais ok pour notre cible kiosque/classe.
//
// /!\ Si tu changes cette logique, vérifie que VitrineKiosk + ThreeGame + tout
// composant qui rend un avatar passent par cette fonction — sinon le joueur verra
// un avatar dans le lobby et un autre une fois la partie lancée.
export function avatarFileForPlayer(playerId: string): string {
  let hash = 0;
  for (let i = 0; i < playerId.length; i++) {
    hash = (hash * 31 + playerId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_LIST.length;
  return `/assets/avatars3D/${AVATAR_LIST[idx]}.glb`;
}

// Liste des avatars disponibles (fichiers GLB dans /assets/avatars3D/)
export const AVATAR_LIST = [
  "animal-beaver",
  "animal-bee",
  "animal-bunny",
  "animal-cat",
  "animal-caterpillar",
  "animal-chick",
  "animal-cow",
  "animal-crab",
  "animal-deer",
  "animal-dog",
  "animal-elephant",
  "animal-fish",
  "animal-fox",
  "animal-giraffe",
  "animal-hog",
  "animal-koala",
  "animal-lion",
  "animal-monkey",
  "animal-panda",
  "animal-parrot",
  "animal-penguin",
  "animal-pig",
  "animal-polar",
  "animal-tiger",
];

// Unités monde par step de jeu (position du joueur)
export const UNITS_PER_STEP = 2.5;

// Espacement Z entre les lanes
export const LANE_SPACING = 2.5;

// Caméra latérale — spectateur (vue large de côté)
export const CAMERA_HEIGHT = 35;
export const CAMERA_DISTANCE = 35;
export const CAMERA_LERP_SPEED = 0.06;
export const CAMERA_LOOK_AHEAD = 3;

// Caméra mobile — joueur (vue rapprochée 3/4 arrière)
export const MOBILE_CAM_HEIGHT = 4.5;
export const MOBILE_CAM_DISTANCE_Z = 7;
export const MOBILE_CAM_BEHIND_X = -3.5;
export const MOBILE_CAM_LOOK_HEIGHT = 1.5;
// Décalage vertical du lookAt : valeur négative => caméra vise plus bas =>
// l'avatar remonte dans le cadre (laisse de la place pour l'UI Quiz fixée en bas).
export const MOBILE_CAM_LOOK_OFFSET_Y = -1.0;
// Look-ahead horizontal du lookAt. 0 => la caméra reste centrée sur l'avatar
// (sinon, à valeur > 0, le joueur paraît "lâché" derrière quand il accélère).
export const MOBILE_CAM_LOOK_AHEAD = 0;
export const MOBILE_CAM_LERP_SPEED = 0.08;

// Mouvement personnage — spring-damper pour tween fluide
export const SPRING_STIFFNESS = 12;
export const SPRING_DAMPING = 8;
export const WALK_THRESHOLD = 0.5;
export const RUN_THRESHOLD = 5.0;

// Échelle du modèle
export const MODEL_SCALE = 1.2;

// --- Podium de fin de partie ---
//
// GLB DÉDIÉ, volontairement pas dans Scene_game.glb : ce dernier est tuilé N fois
// le long de la piste par Decor.tsx, un podium dedans apparaîtrait à chaque tuile.
// Le fichier est OPTIONNEL — tant qu'il n'existe pas, la fin de partie garde
// l'ancien zoom cinématique sur le winner (cf. usePodium dans Podium.tsx).
//
// Convention de nommage attendue dans le GLB : un empty par place, `Place_1`,
// `Place_2`, `Place_3` pour les marches, puis `Place_4`, `Place_5`... pour les
// suivants (casse libre). Le rang N va sur `Place_N`. Un rang sans spot reste
// où il est.
export const PODIUM_URL = "/bg/Podium_retake.glb";

// Décodeur Draco auto-hébergé. L'asset est compressé KHR_draco_mesh_compression
// (sortie glTF-Transform) : sans DRACOLoader, GLTFLoader jette
// "No DRACOLoader instance provided" et le podium ne charge jamais. Les fichiers
// viennent de three/examples/jsm/libs/draco/gltf, copiés dans public/draco pour
// ne pas dépendre du CDN Google (cohérent avec le self-host de Nunito).
export const DRACO_DECODER_PATH = "/draco/";

// Échelle du podium. Réglée sur l'espacement des MARCHES, pas des places au sol :
// dans l'asset, Place_1/2/3 ne sont écartées que de 1.29 unité GLB alors que les
// places au sol le sont de 0.52. À l'échelle 4 les trois avatars du podium se
// chevauchaient franchement (vérifié en capture). 6.5 porte l'écart des marches à
// ~2.1 unités monde, soit la largeur d'un avatar, et les places au sol à ~3.4.
export const PODIUM_SCALE = 6.5;

// Le podium n'est PAS posé au bout de la piste. L'asset est un diorama complet
// (montagne, château, arbres : 327 x 55 x 273 unités monde à l'échelle ci-dessus)
// qui engloutirait la piste et le décor tuilé. On l'installe donc très à l'écart,
// dans une zone vide du monde, et la caméra y coupe pendant le flash blanc.
// Track et Decor sont démontés à ce moment-là, personne ne voit le saut.
export const PODIUM_STAGE_X = -2000;

// Séquence du flash blanc. La téléportation des avatars se fait pendant le plateau
// blanc, donc invisible : on ne voit que la course, puis le podium.
export const PODIUM_FLASH_IN_MS = 260;
export const PODIUM_FLASH_HOLD_MS = 140;
export const PODIUM_FLASH_OUT_MS = 700;

// Délai avant que le leaderboard mobile (overlay plein écran de Quiz.tsx) recouvre
// la scène. Sans ça le podium serait masqué à la seconde même où il apparaît.
export const PODIUM_REVEAL_MS = 4200;

// Orientation des avatars une fois sur le podium. Les places sont alignées sur X
// (étalement 16.7 unités, Z constant), donc la "photo de groupe" se prend depuis
// +Z : les avatars doivent regarder +Z et non +X comme pendant la course.
// 0 = orientation naturelle du modèle (+Z) ; la course utilise Math.PI / 2 (+X).
export const PODIUM_MODEL_YAW = 0;

// Cadrage de repli, utilisé tant que le GLB n'embarque pas de caméra (ni node
// glTF camera, ni empty nommé Camera/View). La caméra se recule assez pour
// contenir les places OCCUPÉES, pas les 10.
//
// Hauteurs calées sur le sujet réel à PODIUM_SCALE=6.5 : marche haute à y≈2.9,
// avatar ≈2.5 de haut, étiquette de nom +2.6 au-dessus. Le sujet monte donc à
// ~8 unités. Viser 1.4 comme avant laissait la moitié basse du cadre en sol vide
// et coupait les noms en haut (vérifié en capture).
export const PODIUM_CAM_HEIGHT = 5.4;
export const PODIUM_CAM_LOOK_HEIGHT = 3.2;
// Distance minimale et marge appliquée à la largeur occupée. Le plancher couvre
// le cas d'une partie à 1-2 joueurs, où la largeur ne dicte plus rien et où c'est
// la HAUTEUR du sujet qui impose le recul.
export const PODIUM_CAM_MIN_DISTANCE = 13;
export const PODIUM_CAM_WIDTH_MARGIN = 1.35;

// Plan de clipping lointain pendant la phase podium. Le Canvas du jeu tourne à
// far=200, suffisant pour la piste, mais le diorama du podium s'étend à ~220
// unités de son centre à PODIUM_SCALE=6.5 : la montagne et le château (tout ce
// qui fait la photo) seraient purement et simplement clippés. SideCamera relève
// donc far à l'entrée sur le podium et le restaure en sortie.
export const PODIUM_CAM_FAR = 700;
export const DEFAULT_CAM_FAR = 200;

// Couleurs joueurs
export const PLAYER_COLORS = [
  "#ff6b9d",
  "#c084fc",
  "#7dd3fc",
  "#fbbf24",
  "#6ee7b7",
  "#f472b6",
];
